import { OTLayout } from "@/components/layout/ot-layout";
import { useState, useEffect, useRef } from "react";
import { Shield, Calendar, CheckSquare, Square, ChevronDown, ChevronUp, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { Link } from "wouter";

const CHECKLIST_KEY = "ot_hcpc_checklist";

const CHECKLIST_ITEMS = [
  { id: "profile", label: "I have maintained a CPD profile", standard: "Standard 1" },
  { id: "range", label: "I have undertaken a range of CPD activities", standard: "Standard 2" },
  { id: "benefit", label: "My CPD activities have benefited the people who use my services", standard: "Standard 3" },
  { id: "summary", label: "I have a summary of my CPD and can explain its relevance to my practice", standard: "Standard 4" },
  { id: "revalidation", label: "I can demonstrate how my CPD has informed and improved my practice", standard: "Standard 5 (OT specific)" },
];

const SOP_SECTIONS = [
  {
    title: "Professional autonomy and accountability",
    content: "Occupational therapists must practise within their scope of competence, maintain fitness to practise, and accept personal responsibility for their professional conduct and actions. OTs must be able to justify any actions and decisions.",
  },
  {
    title: "Occupational science and occupational therapy",
    content: "Must understand and apply the theoretical basis of occupational therapy, including occupational science, occupational performance, and the relationship between occupation, health, and wellbeing.",
  },
  {
    title: "Assessment, intervention and evaluation",
    content: "Must gather appropriate information, formulate clinical reasoning, plan and deliver evidence-based occupational therapy interventions, and evaluate outcomes systematically with the person.",
  },
  {
    title: "Communication, partnership and teamworking",
    content: "Must communicate effectively with service users, carers, and the wider MDT; involve people in shared decision-making about their occupational goals and therapy interventions.",
  },
  {
    title: "Equality, diversity and inclusion",
    content: "Must promote equality, value diversity, and ensure services are accessible and culturally sensitive, adapting occupational therapy approaches to meet individual needs without discrimination.",
  },
  {
    title: "Research and service development",
    content: "Must apply evidence-based practice, engage with research and audit, and contribute to quality improvement and service development to enhance occupational therapy outcomes.",
  },
];

function daysUntil(dateStr: string): number {
  const target = new Date(dateStr);
  const today = new Date();
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export default function OtHcpcRevalidation() {
  const [renewalDate, setRenewalDate] = useState("");
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [checklist, setChecklist] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(CHECKLIST_KEY) ?? "{}"); }
    catch { return {}; }
  });
  const [openSop, setOpenSop] = useState<number | null>(null);
  const [cpdCount, setCpdCount] = useState<number | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      setSettingsLoading(true);
      try {
        const res = await fetch("/api/ot/settings");
        if (res.ok) {
          const data = await res.json();
          if (data.hcpcRenewalDate) setRenewalDate(data.hcpcRenewalDate);
        }
      } finally {
        setSettingsLoading(false);
      }
    };
    loadSettings();

    fetch("/api/ot/cpd/summary")
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCpdCount(d.count); });
  }, []);

  useEffect(() => {
    localStorage.setItem(CHECKLIST_KEY, JSON.stringify(checklist));
  }, [checklist]);

  const handleRenewalDateChange = (val: string) => {
    setRenewalDate(val);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSettingsSaving(true);
      try {
        await fetch("/api/ot/settings", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ hcpcRenewalDate: val || null }),
        });
      } finally {
        setSettingsSaving(false);
      }
    }, 800);
  };

  const toggleCheck = (id: string) => {
    setChecklist(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const checkedCount = CHECKLIST_ITEMS.filter(i => checklist[i.id]).length;
  const days = renewalDate ? daysUntil(renewalDate) : null;

  const countdownColour =
    days === null ? "text-slate-400"
    : days < 0 ? "text-red-600"
    : days < 90 ? "text-orange-600"
    : days < 180 ? "text-amber-600"
    : "text-emerald-600";

  return (
    <OTLayout title="HCPC Revalidation Dashboard">
      <div className="space-y-6">

        <div className="bg-gradient-to-br from-amber-800 to-orange-900 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-7 w-7 text-amber-300" />
            <div>
              <h2 className="text-xl font-bold">HCPC Revalidation — Occupational Therapy</h2>
              <p className="text-amber-300 text-sm">2-year renewal cycle · Track your readiness</p>
            </div>
          </div>
          <p className="text-amber-100 text-sm leading-relaxed">
            HCPC requires all registered occupational therapists to demonstrate they meet the CPD standards at each renewal. This dashboard helps you track your readiness and understand what HCPC will look for if you are selected for audit.
          </p>
        </div>

        {/* Renewal Date & Countdown */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="h-5 w-5 text-amber-600" />
            <h3 className="font-bold text-slate-900">Renewal Date</h3>
            {settingsSaving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </div>
          {settingsLoading ? (
            <div className="h-9 bg-slate-100 animate-pulse rounded-lg w-48" />
          ) : (
            <div className="flex items-center gap-6 flex-wrap">
              <input
                type="date"
                value={renewalDate}
                onChange={(e) => handleRenewalDateChange(e.target.value)}
                className="h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              {days !== null && (
                <div className={`text-2xl font-bold ${countdownColour}`}>
                  {days < 0 ? `${Math.abs(days)} days overdue` : days === 0 ? "Due today" : `${days} days remaining`}
                </div>
              )}
              {days === null && (
                <p className="text-sm text-slate-400">Set your renewal date to see the countdown</p>
              )}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-3">Your HCPC registration renewal date is shown on your HCPC Online account. The CPD audit period covers the 2 years prior to renewal.</p>
        </div>

        {/* CPD Completeness */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900">CPD Log Completeness</h3>
            <Link href="/ot/cpd">
              <span className="text-sm text-amber-700 hover:text-amber-800 font-medium cursor-pointer">View CPD Log →</span>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-3xl font-bold text-amber-700">{cpdCount ?? "–"}</div>
            <div>
              <p className="text-sm font-medium text-slate-700">CPD activities logged this year</p>
              <p className="text-xs text-slate-500">HCPC expects a profile of CPD across the 2-year renewal period — there is no fixed minimum number of hours.</p>
            </div>
          </div>
        </div>

        {/* HCPC Audit Checklist */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900">HCPC CPD Audit Readiness Checklist</h3>
            <span className="text-sm font-medium text-slate-500">{checkedCount}/{CHECKLIST_ITEMS.length} complete</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2 mb-5">
            <div
              className="bg-amber-500 h-2 rounded-full transition-all"
              style={{ width: `${(checkedCount / CHECKLIST_ITEMS.length) * 100}%` }}
            />
          </div>
          <div className="space-y-3">
            {CHECKLIST_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => toggleCheck(item.id)}
                className="flex items-start gap-3 w-full text-left p-3 rounded-xl hover:bg-slate-50 transition-colors"
              >
                {checklist[item.id]
                  ? <CheckSquare className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  : <Square className="h-5 w-5 text-slate-300 shrink-0 mt-0.5" />}
                <div>
                  <p className={`text-sm font-medium ${checklist[item.id] ? "text-slate-500 line-through" : "text-slate-800"}`}>
                    {item.label}
                  </p>
                  <p className="text-xs text-slate-400">{item.standard}</p>
                </div>
              </button>
            ))}
          </div>
          {checkedCount === CHECKLIST_ITEMS.length && (
            <div className="mt-4 flex items-center gap-2 bg-emerald-50 rounded-xl p-3 text-emerald-700">
              <CheckCircle className="h-5 w-5 shrink-0" />
              <p className="text-sm font-medium">All standards met — you are ready for HCPC audit.</p>
            </div>
          )}
          {checkedCount < CHECKLIST_ITEMS.length && (
            <div className="mt-4 flex items-center gap-2 bg-amber-50 rounded-xl p-3 text-amber-700">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <p className="text-sm">Complete your CPD log and tick off each standard as you meet it.</p>
            </div>
          )}
        </div>

        {/* HCPC Standards of Proficiency Reference */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">HCPC Standards of Proficiency — Occupational Therapists</h3>
          <p className="text-sm text-slate-500 mb-4">Reference summary of the key professional standards you must meet throughout your career. Expand each area for detail.</p>
          <div className="space-y-2">
            {SOP_SECTIONS.map((s, i) => (
              <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenSop(openSop === i ? null : i)}
                  className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="text-sm font-medium text-slate-800">{s.title}</span>
                  {openSop === i ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </button>
                {openSop === i && (
                  <div className="px-4 pb-4 pt-1 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                    {s.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>Important:</strong> This dashboard is for personal tracking only and does not replace the formal HCPC renewal process. Always renew your registration via HCPC Online. CPD audit selection is random — approximately 2.5% of OTs are audited at each renewal. Records maintained here can support your formal audit submission.
        </div>
      </div>
    </OTLayout>
  );
}
